import { navigate } from 'gatsby';
import { useEffect, useState } from 'react';
import { services } from '../context';
import { useStateDispatch, useStateSelector } from '../store';
import {
  clearBooking,
  clearSelected,
  clearSelectedStore,
  setOperation,
  updateSelectedService,
} from '../reducers/app/appReducer';
import { getServices } from '../reducers/services/servicesReducer';
import { isStoreService } from '../helpers';
import { getService } from '../helpers/serviceHelper';
import { getStores } from '../reducers/stores/storesReducer';
import {
  addPersistingQueryParamsToPath,
  getPagePath,
  OPERATION_SUB_PAGES,
  OperationPath,
} from '../helpers/urlHelper';
import { clearSessionData } from '../helpers/sessionHelper';
import { setCookieForNativeApp } from '../helpers/nativeAppHelper';
import { clearAvailableTimeSlots } from '../reducers/timeslots/timeSlotsReducer';
import { CustomerType } from '../reducers/user/userTypes';
import {
  selectAppOperation,
  selectSelectedTimeSlot,
} from '../reducers/selectors';

type Page = OperationPath | '';
const PAGES_REQUIRING_SERVICES_DATA: Page[] = [
  OPERATION_SUB_PAGES.CONFIRM,
  OPERATION_SUB_PAGES.CONTACT,
  OPERATION_SUB_PAGES.TIME_SLOTS,
];

const shouldFetchData = (page: Page): boolean => {
  return PAGES_REQUIRING_SERVICES_DATA.includes(page);
};

const navigateToIndexPage = (operation: string): void => {
  const path = getPagePath(operation);
  return navigate(addPersistingQueryParamsToPath(path));
};

const fetchPageData = async (
  dispatch: ReturnType<typeof useStateDispatch>,
  params: {
    customerType: CustomerType;
    allowedMemberDiscounts: boolean;
    operation: string;
    zipCode: string;
    serviceId: string;
    stateCode?: string;
  },
): Promise<void> => {
  const {
    customerType,
    allowedMemberDiscounts,
    operation,
    zipCode,
    stateCode,
    serviceId,
  } = params;

  try {
    const availableServices = await dispatch(
      getServices({
        customerType,
        allowedMemberDiscounts,
        operation,
        zipCode,
        stateCode,
      }),
    ).unwrap();
    const selectedService = getService(serviceId, availableServices);
    if (!selectedService) {
      throw new Error('Service not found');
    }

    dispatch(
      updateSelectedService({
        serviceId: selectedService.serviceId,
        serviceCode: selectedService.serviceCode,
      }),
    );

    if (selectedService && isStoreService(selectedService.location)) {
      await dispatch(
        getStores({ zipCode, serviceProductId: selectedService.serviceId }),
      ).unwrap();
    }
  } catch (error) {
    dispatch(clearSelected());
    dispatch(clearBooking());
    clearSessionData();
    navigateToIndexPage(operation);
  }
};

const useInitPage = (
  operation: string,
  isAuthenticating: boolean,
  page: Page = '',
) => {
  const [isInitPage, setIsInitPage] = useState(true);
  const dispatch = useStateDispatch();
  const currentOperation = useStateSelector(selectAppOperation);
  const zipCode = useStateSelector((state) => state.search.postCode);
  const stateCode = useStateSelector((state) => state.search.state);
  const serviceId = useStateSelector((state) => state.app.selected.serviceId);
  const selectedTimeSlot = useStateSelector(selectSelectedTimeSlot);
  const customerType = useStateSelector((state) => state.user.customerType);
  const allowedMemberDiscounts = useStateSelector(
    (state) => state.user.allowedMemberDiscounts,
  );

  useEffect(() => {
    if (isAuthenticating) {
      return;
    }

    const isNewSession = !currentOperation;

    const requestPageData = async () => {
      if (shouldFetchData(page)) {
        await fetchPageData(dispatch, {
          customerType,
          allowedMemberDiscounts,
          operation,
          zipCode,
          serviceId,
          stateCode,
        });
      }
    };

    const handleClientFirstEntry = async () => {
      await services.status.getStatus();

      const isOperationPathPage = !!operation;
      if (isOperationPathPage) {
        await requestPageData();
        dispatch(setOperation(operation));
        setCookieForNativeApp(operation);
      }

      setIsInitPage(false);
    };

    const handleNavigatingToPage = async () => {
      const clearState = async () => {
        if (page === OPERATION_SUB_PAGES.TIME_SLOTS) {
          dispatch(clearAvailableTimeSlots());
          if (selectedTimeSlot?.id) {
            await services.timeSlot.clearReservedTimeSlot(selectedTimeSlot.id);
          }
        }
        if (page === OPERATION_SUB_PAGES.SERVICES) {
          dispatch(clearSelectedStore());
        }
      };

      await clearState();
      setIsInitPage(false);
    };

    if (isNewSession) {
      handleClientFirstEntry();
    } else {
      handleNavigatingToPage();
    }
    // Only run on page load after authentication step is completed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticating]);

  return { isInitPage };
};

export default useInitPage;
